import React, { useState, useEffect, useRef } from "react";
import config from "../config";
import SpadeHomePage from "../pages/Site/SpadeHomePage";
import PricingPage from "../pages/Site/PricingPage";
import ContactUsPage from "../pages/Site/ContactUsPage";
import DemoPage from "../pages/Site/DemoPage";
import TextDataPage from "../pages/Site/TextDataPage";
import CareerPage from "../pages/Site/CareerPage";
import { AppBar, Toolbar, Box } from "@mui/material";
import logo from "../assets/spade-logo.svg";
import { useLocation, useNavigate } from "react-router-dom";

const baseUrl = config.baseURL;

// Pages that don't need a backend fetch — render directly by type
const STATIC_PAGES = ["demo", "pricing"];

const SiteNavbar = () => {
  const [pages, setPages] = useState([]);
  const [currentPageData, setCurrentPageData] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [activeType, setActiveType] = useState(null);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthPath =
    location.pathname.toLowerCase() === "/register" ||
    location.pathname.toLowerCase() === "/login" ||
    location.pathname.toLowerCase() === "/forgot-password" ||
    location.pathname.toLowerCase().startsWith("/reset-password");

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchPages = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/page`);
        if (!res.ok) throw new Error("Error fetching pages");
        const data = await res.json();
        const list = data.data || [];
        // deduplicate by id
        const seen = new Set();
        const unique = list.filter((p) => !seen.has(p.id) && seen.add(p.id));
        setPages(unique);
      } catch (err) {
        console.error("Error fetching pages:", err.message);
      }
    };
    fetchPages();
  }, []);

  const handlePageClick = async (page) => {
    navigate("/");
    setActiveTab(page.id);
    setActiveType(page.type);
    setError(null);
    setCurrentPageData(null);

    // Static pages — render directly without backend fetch
    if (STATIC_PAGES.includes(page.type)) {
      setCurrentPageData({ type: page.type, sub_type: page.sub_type });
      return;
    }

    // Dynamic pages — fetch from backend
    try {
      const res = await fetch(`${baseUrl}/api/page/${page.id}`);
      if (!res.ok) throw new Error(`Failed to load page (${res.status})`);
      const data = await res.json();
      setCurrentPageData(data.data || data);
    } catch (err) {
      console.error("Error fetching page data:", err.message);
      setError(err.message);
    }
  };

  const goHome = () => {
    setActiveTab("home");
    setActiveType(null);
    setCurrentPageData(null);
    setError(null);
  };

  // Allows child pages to trigger navbar navigation by type
  const navigateToType = (type) => {
    const target = pages.find((p) => p.type === type);
    if (target) handlePageClick(target);
  };

  const renderPage = () => {
    if (error) {
      return (
        <div style={{ textAlign: "center", padding: "80px 24px" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
          <p style={{ color: "#64748b", fontSize: 15, marginBottom: 20 }}>
            {error}
          </p>
          <button
            onClick={goHome}
            style={{
              padding: "10px 24px",
              background: "#2E4053",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Go Home
          </button>
        </div>
      );
    }

    switch (currentPageData?.type) {
      case "career":
        return <CareerPage currentPageData={currentPageData} />;
      case "pricing":
        return <PricingPage currentPageData={currentPageData} />;
      case "contact_us":
        return (
          <ContactUsPage
            currentPageData={currentPageData}
            onNavigateTo={navigateToType}
          />
        );
      case "demo":
        return <DemoPage currentPageData={currentPageData} />;
      case "textData":
        return <TextDataPage currentPageData={currentPageData} />;
      default:
        return <SpadeHomePage />;
    }
  };

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#2E4053",
          height: "80px",
          boxShadow: "0px 1px 10px 5px #1a1a1a1a",
        }}
      >
        <Toolbar sx={{ height: "100%", minHeight: "80px !important" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              cursor: "pointer",
            }}
            onClick={goHome}
          >
            <img
              src={logo}
              alt="Spade ERP"
              style={{ height: 170, width: 170, marginRight: 10 }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "24px" }}>
            <span
              onClick={goHome}
              style={{
                cursor: "pointer",
                fontSize: 14,
                transition: "color .15s",
                color: activeTab === "home" ? "#fff" : "#D1D8E0",
                fontWeight: activeTab === "home" ? 700 : 400,
              }}
            >
              Home
            </span>

            {pages.map((page) => (
              <span
                key={page.id}
                onClick={() => handlePageClick(page)}
                style={{
                  cursor: "pointer",
                  fontSize: 14,
                  transition: "color .15s",
                  whiteSpace: "nowrap",
                  color: activeTab === page.id ? "#fff" : "#D1D8E0",
                  fontWeight: activeTab === page.id ? 700 : 400,
                }}
              >
                {page.title}
              </span>
            ))}

            <button
              onClick={() => navigate("/register")}
              style={{
                background: "#fff",
                color: "#2E4053",
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Start Free →
            </button>
          </Box>
        </Toolbar>
      </AppBar>

      {!isAuthPath && <Box sx={{ marginTop: "80px" }}>{renderPage()}</Box>}
    </Box>
  );
};

export default SiteNavbar;
